import FileColumn from "../pages/Reports/Columns/FileColumn"
import { useTranslation } from "react-i18next"
import { useMap } from "./useMap"
import FuelDeviationColumn from "../pages/Reports/Columns/FuelDeviationColumn"
import { REPORTCOLS } from "../pages/Reports/Data/columnData"
import { FORMAT_TYPE } from "../data/variables"
import DynamicColumn from "../pages/Reports/Columns/DynamicColumn"
import { DateTime } from "luxon"
import { formatNumber } from "../services/utils/misc"

export type ReportOptions = {
  changeRow?: (id: string, change: any) => void
  headerId?: string
  dynamicType?: 'year' | 'month'| 'week'
  format?: string
  isDynamic?: boolean
}

export const useFormat = () => {
  const { t } = useTranslation()
  const { milesToKilometers } = useMap()
  
  const gradeToLetter = (grade: number) => {
    if (grade <= 0.1) return 'A'
    else if (grade <= 0.3) return 'B'
    else if (grade <= 0.5) return 'C'
    else if (grade <= 0.7) return 'D'
    else if (grade <= 0.9) return 'E'
    return 'F'
  }

  const localizeNumbers = (value: number, decimal: number) => {
    return value?.toLocaleString(undefined, {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal,
    })
  }

  const stringToHtml = (str: string, classes: string) => {
    return (
      <div className={classes} dangerouslySetInnerHTML={{ __html: str }} />
    )
  }

  const formatFunction = (
    val: any, row: any, key: string, type: string, options: ReportOptions = {}
  ) => {
    if (key === REPORTCOLS.FILE) {
      return <FileColumn value={val} url={row.file_link} />
    }  else if (key === REPORTCOLS.AVERAGE_FUEL_CONSUMPTION || key === REPORTCOLS.VOLUME) {
      if (!val) return t('data missing')
      return val.toFixed(1)
    } else if (key === REPORTCOLS.DEVIATION_RECORDED) {
      if (val > 100) {
        return (
          <FuelDeviationColumn data={row} />
        )
      }
      return null
    } else if (key === REPORTCOLS.RESPONSIBLE || key === REPORTCOLS.SPEEDING) {
      return gradeToLetter(val ?? 0)
    } else if (key === REPORTCOLS.TOTAL_EMISSIONS) {
      const valInKg = val / 1000
      return `${valInKg.toFixed(1)} kg`
    } else if (key === REPORTCOLS.SPEEDLIMIT || key === REPORTCOLS.SPEED) {
      return `${val} ${t('kph')}`
    } else if (options.isDynamic) {
      return (
        <DynamicColumn
          value={val}
          row={row}
          options={options}
          type={options.dynamicType}
        />
      )
    } else if (type === FORMAT_TYPE.NUMBER) {
      return val?.toLocaleString() ?? ''
    } else if (type === 'minutes') {
      const minutes = Math.floor(val / 60)
      const seconds = Math.round(val % 60)
      return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds} ${t('mins')}`
    } else if (type === FORMAT_TYPE.BOOLEAN) {
      return !val ? t('No') : t('Yes')
    } else if (type === FORMAT_TYPE.DATETIME) {
      const isValid = val ? DateTime.fromISO(val).isValid : false
      return isValid ? DateTime.fromISO(val).toFormat('LLL d, yyyy HH:mm') : val
    } else if (type === FORMAT_TYPE.METERS) {
      return `${Math.round(val).toFixed(0)} m`
    } else if (type === FORMAT_TYPE.KILOMETERS) {
      if (val < 1) return `${formatNumber(val * 1000)} m`
      return `${formatNumber(val)} km`
    } else if (type === FORMAT_TYPE.DATE) {
      const isValid = val ? DateTime.fromISO(val).isValid : false
      return isValid ? DateTime.fromISO(val).toFormat('LLL d, yyyy') : val
    } else if (type === FORMAT_TYPE.TIME) {
      if (!val) return '00:00'
      const isValid = val ? DateTime.fromISO(val).isValid : false
      return isValid ? DateTime.fromISO(val).toFormat('HH:mm') : val
    } else if (type === FORMAT_TYPE.DISTANCE) {
      return val > 1000 ? `${Math.round(val / 1000).toFixed(0)} km` : `${Math.round(val).toFixed(0)} m`
    } else if (type === FORMAT_TYPE.DURATION) {
      if (!val) return key === REPORTCOLS.TOTAL_IDLE_TIME ? null : `00:00 ${t('hrs')}`
      let minutes = val / 60
      const hours = Math.floor(minutes / 60)
      if (key === REPORTCOLS.DURATION) {
        minutes = Math.floor(minutes - (hours * 60))
        const seconds = val - (minutes * 60) - (hours * 360)
        const secondsFormat = `${seconds < 10 ? `0${seconds}` : seconds}`
        return `${hours}:${minutes < 10 ? `0${minutes}` : minutes}:${secondsFormat} ${t('hrs')}`
      }
      minutes = Math.round(minutes - (hours * 60))
      return `${hours}:${minutes < 10 ? `0${minutes}` : minutes} ${t('hrs')}`
    } else if (type === FORMAT_TYPE.SPEED) {
      return `${milesToKilometers(val)} ${t('kph')}`
    } else if (type === FORMAT_TYPE.HOURS) {
      let hours = val
      if (!hours) hours = '00:00:00' 
      return `${hours} ${t('hrs')}`
    }

    return val
  }

  return {
    formatFunction,
    gradeToLetter,
    localizeNumbers,
    stringToHtml
  }
}
